import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MessageCircle,
  Send,
  X,
  Clock,
  Plus,
  Loader2,
  Paperclip,
  FileText,
  Download,
  Search,
  ArrowLeft,
  Mail,
  Check,
  CheckCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from '../../config';
import './ChatContent.css';

const LIST_POLL_MS = 10000;
const THREAD_POLL_MS = 5000;
const NEAR_BOTTOM_PX = 120;

function ChatContent({ userRole, initialConversationId, onUnreadChange }) {
  const { t, i18n } = useTranslation();
  const isAdmin = userRole === "admin";

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [showNewConvModal, setShowNewConvModal] = useState(false);
  const [creatingConv, setCreatingConv] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  // Inbox controls
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [sort, setSort] = useState("recent");

  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const selectedIdRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  }), []);

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (userId) {
      setCurrentUserId(parseInt(userId));
    }
  }, []);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadConversations = useCallback(async () => {
    try {
      const params = new URLSearchParams({ filter: listFilter, sort, per_page: "50" });
      if (search) {
        params.set("search", search);
      }
      const response = await fetch(
        `${API_BASE_URL}/chat/conversations/?${params.toString()}`,
        { headers: authHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error("Failed to load conversations:", error);
    }
  }, [authHeaders, listFilter, sort, search]);

  // Reload the list whenever the controls change, then poll it while visible.
  useEffect(() => {
    loadConversations();
    const interval = setInterval(() => {
      if (!document.hidden) {
        loadConversations();
      }
    }, LIST_POLL_MS);
    return () => clearInterval(interval);
  }, [loadConversations]);

  const loadOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error("Failed to load orders:", error);
    }
  };

  const createConversationForOrder = async (orderId) => {
    try {
      setCreatingConv(true);
      const response = await fetch(`${API_BASE_URL}/chat/conversations/`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId }),
      });

      const data = await response.json();
      if (data.success) {
        setShowNewConvModal(false);
        await loadConversations();
        if (data.conversation?.id) {
          loadConversationDetail(data.conversation.id);
        }
        setMessage(t("chatContent.conversationStarted"));
      } else {
        setMessage(`❌ ${data.error || t("chatContent.failedCreateConversation")}`);
      }
    } catch (error) {
      console.error("Failed to create conversation:", error);
      setMessage(t("chatContent.failedCreateConversationWithEmoji"));
    } finally {
      setCreatingConv(false);
    }
  };

  const loadConversationDetail = useCallback(async (conversationId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/chat/conversations/${conversationId}/`,
        { headers: authHeaders() }
      );

      const data = await response.json();
      if (data.success) {
        const conversation = data.conversation;
        const thread = conversation.messages || [];
        setSelectedConversation(conversation);
        setMessages(thread);
        selectedIdRef.current = conversation.id;
        lastMessageIdRef.current = thread.length ? thread[thread.length - 1].id : null;
        shouldStickToBottomRef.current = true;

        // Opening a thread marks it read, so refresh the list and the nav badge.
        loadConversations();
        if (onUnreadChange) {
          onUnreadChange();
        }

        if (window?.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set("conversation", conversationId);
          window.history.replaceState({}, "", url);
        }
      }
    } catch (error) {
      console.error("Failed to load conversation:", error);
      setMessage(t("chatContent.failedLoadConversation"));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, loadConversations, onUnreadChange, t]);

  useEffect(() => {
    if (initialConversationId) {
      loadConversationDetail(initialConversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId]);

  // Poll the OPEN thread for new messages. Previously only the list refreshed, so
  // a reply never arrived while you were sitting in the conversation.
  useEffect(() => {
    if (!selectedConversation) return undefined;

    const conversationId = selectedConversation.id;
    const poll = async () => {
      if (document.hidden) return;
      try {
        const after = lastMessageIdRef.current;
        const params = after ? `?after=${after}` : "";
        const response = await fetch(
          `${API_BASE_URL}/chat/conversations/${conversationId}/messages/${params}`,
          { headers: authHeaders() }
        );
        const data = await response.json();
        if (!data.success || selectedIdRef.current !== conversationId) return;

        const incoming = data.messages || [];
        if (incoming.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const fresh = incoming.filter((m) => !seen.has(m.id));
            return fresh.length ? [...prev, ...fresh] : prev;
          });
          lastMessageIdRef.current = incoming[incoming.length - 1].id;
          loadConversations();
          if (onUnreadChange) {
            onUnreadChange();
          }
        }
      } catch (error) {
        console.error("Failed to poll messages:", error);
      }
    };

    const interval = setInterval(poll, THREAD_POLL_MS);
    return () => clearInterval(interval);
  }, [selectedConversation, authHeaders, loadConversations, onUnreadChange]);

  // Only follow new messages if the reader is already at the bottom, so polling
  // never yanks someone out of scrollback.
  const handleMessagesScroll = () => {
    const el = messagesAreaRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldStickToBottomRef.current = distance < NEAR_BOTTOM_PX;
  };

  useEffect(() => {
    if (shouldStickToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setMessage(t("chatContent.fileSizeLimit"));
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    try {
      setLoading(true);

      const formData = new FormData();
      if (newMessage.trim()) {
        formData.append('message', newMessage);
      }
      if (selectedFile) {
        formData.append('attachment', selectedFile);
      }

      const response = await fetch(
        `${API_BASE_URL}/chat/conversations/${selectedConversation.id}/`,
        { method: "POST", headers: authHeaders(), body: formData }
      );

      const data = await response.json();
      if (data.success) {
        setNewMessage("");
        clearSelectedFile();
        setMessage("");
        shouldStickToBottomRef.current = true;
        if (data.data) {
          setMessages((prev) => (
            prev.some((m) => m.id === data.data.id) ? prev : [...prev, data.data]
          ));
          lastMessageIdRef.current = data.data.id;
        }
        loadConversations();
      } else {
        setMessage(`❌ ${data.error || t("chatContent.failedSend")}`);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setMessage(t("chatContent.failedSendWithEmoji"));
    } finally {
      setLoading(false);
    }
  };

  const closeThread = () => {
    setSelectedConversation(null);
    setMessages([]);
    selectedIdRef.current = null;
    lastMessageIdRef.current = null;
    if (window?.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete("conversation");
      window.history.replaceState({}, "", url);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });
  };

  const formatFullTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString(i18n.language, {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  /** Short, scannable age for list rows: "5m", "3h", "Yesterday", "14 Mar". */
  const formatRelative = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);

    if (diffMin < 1) return t("chatContent.justNow");
    if (diffMin < 60) return t("chatContent.minutesAgo", { count: diffMin });

    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return t("chatContent.hoursAgo", { count: diffHr });

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return t("chatContent.yesterday");
    }

    return date.toLocaleDateString(i18n.language, { day: "numeric", month: "short" });
  };

  const formatDaySeparator = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return t("chatContent.today");
    if (date.toDateString() === yesterday.toDateString()) return t("chatContent.yesterday");
    return date.toLocaleDateString(i18n.language, {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  };

  /** Who the row is "with": admins look at customers, customers look at support. */
  const counterpartName = (conv) => (
    isAdmin
      ? conv.customer_name || conv.customer_username
      : conv.admin_name || conv.admin_username || t("chatContent.unassigned")
  );

  const previewText = (conv) => {
    if (!conv.last_message) return t("chatContent.noMessagesYet");
    const isOwn = conv.last_message.sender_id === currentUserId;
    const body = conv.last_message.content
      || (conv.last_message.has_attachment ? conv.last_message.attachment_name : "");
    const prefix = isOwn ? `${t("chatContent.you")}: ` : "";
    return `${conv.last_message.has_attachment ? "📎 " : ""}${prefix}${body}`;
  };

  const filterOptions = isAdmin
    ? [
        { key: "all", label: t("chatContent.filterAll") },
        { key: "unread", label: t("chatContent.filterUnread") },
        { key: "unassigned", label: t("chatContent.filterUnassigned") },
      ]
    : [
        { key: "all", label: t("chatContent.filterAll") },
        { key: "unread", label: t("chatContent.filterUnread") },
      ];

  const newConversationButton = (extraClass = "") => (
    <button
      onClick={() => {
        loadOrders();
        setShowNewConvModal(true);
      }}
      className={`new-conversation-btn ${extraClass}`}
    >
      <Plus size={14} />
      {t("chatContent.newConversation")}
    </button>
  );

  return (
    <div className={`chat-container ${selectedConversation ? "thread-open" : ""}`}>
      {/* Conversations List */}
      <div className="chat-sidebar">
        <div className="chat-header">
          <MessageCircle size={24} />
          <h2>{t("chatContent.messages")}</h2>
        </div>

        <div className="chat-controls">
          {isAdmin && (
            <div className="chat-search">
              <Search size={15} />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("chatContent.searchPlaceholder")}
              />
              {searchInput && (
                <button onClick={() => setSearchInput("")} className="chat-search-clear">
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          <div className="chat-filters">
            {filterOptions.map((option) => (
              <button
                key={option.key}
                className={`chat-filter-chip ${listFilter === option.key ? "active" : ""}`}
                onClick={() => setListFilter(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {isAdmin && (
            <select
              className="chat-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="recent">{t("chatContent.sortRecent")}</option>
              <option value="unread">{t("chatContent.sortUnread")}</option>
              <option value="oldest">{t("chatContent.sortOldest")}</option>
            </select>
          )}
        </div>

        <div className="conversations-list">
          {conversations.length === 0 ? (
            <div className="empty-state">
              <MessageCircle size={40} color="#D1D5DB" />
              <p>
                {search || listFilter !== "all"
                  ? t("chatContent.noMatchingConversations")
                  : t("chatContent.noConversations")}
              </p>
              {!isAdmin && newConversationButton("compact")}
            </div>
          ) : (
            <>
              {!isAdmin && newConversationButton()}
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`conversation-item ${selectedConversation?.id === conv.id ? "active" : ""} ${conv.has_unread ? "unread" : ""}`}
                  onClick={() => loadConversationDetail(conv.id)}
                >
                  <div className="conversation-header">
                    <h4>
                      {conv.has_unread && <span className="unread-dot" />}
                      {counterpartName(conv)}
                    </h4>
                    <span className="conversation-time">
                      {formatRelative(conv.last_message_at || conv.updated_at)}
                    </span>
                  </div>

                  {isAdmin && conv.customer_email && (
                    <p className="conversation-email">
                      <Mail size={11} />
                      {conv.customer_email}
                    </p>
                  )}

                  <div className="conversation-meta">
                    <span className="order-ref">{conv.order_number}</span>
                    <span className="status-badge" data-status={conv.order_status}>
                      {conv.order_status}
                    </span>
                    {isAdmin && !conv.admin && (
                      <span className="unassigned-badge">{t("chatContent.unassigned")}</span>
                    )}
                  </div>

                  <div className="conversation-footer">
                    <p className="last-message">{previewText(conv)}</p>
                    {conv.unread_count > 0 && (
                      <span className="unread-badge">{conv.unread_count}</span>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="chat-main">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="chat-top-header">
              <button onClick={closeThread} className="back-btn" title={t("chatContent.back")}>
                <ArrowLeft size={20} />
              </button>
              <div className="chat-top-identity">
                <h3>{counterpartName(selectedConversation)}</h3>
                <p className="chat-subtitle">
                  {isAdmin && selectedConversation.customer_email && (
                    <span className="chat-subtitle-email">
                      <Mail size={11} />
                      {selectedConversation.customer_email}
                    </span>
                  )}
                  <span className="order-ref">{selectedConversation.order_number}</span>
                  {selectedConversation.order_status && (
                    <span className="status-badge" data-status={selectedConversation.order_status}>
                      {selectedConversation.order_status}
                    </span>
                  )}
                </p>
              </div>
              <button onClick={closeThread} className="close-btn">
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div
              className="messages-area"
              ref={messagesAreaRef}
              onScroll={handleMessagesScroll}
            >
              {messages.length === 0 ? (
                <div className="empty-messages">
                  <MessageCircle size={48} color="#D1D5DB" />
                  <p>{t("chatContent.noMessages")}</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const previous = messages[index - 1];
                  const showDay = !previous
                    || new Date(previous.created_at).toDateString()
                      !== new Date(msg.created_at).toDateString();
                  const isOwn = msg.sender_id === currentUserId;

                  return (
                    <React.Fragment key={msg.id}>
                      {showDay && (
                        <div className="day-separator">
                          <span>{formatDaySeparator(msg.created_at)}</span>
                        </div>
                      )}
                      <div className={`message ${msg.is_admin ? "admin" : "user"} ${isOwn ? "own" : ""}`}>
                        <div className="message-bubble">
                          {!isOwn && (
                            <span className="message-sender">
                              {msg.sender_username}
                            </span>
                          )}
                          {msg.content && <p>{msg.content}</p>}
                          {msg.has_attachment && msg.attachment_url && (
                            <div className="message-attachment">
                              {msg.attachment_type === 'image' ? (
                                <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer" className="attachment-image-link">
                                  <img src={msg.attachment_url} alt={msg.attachment_name || t("chatContent.attachment")} className="attachment-image" />
                                </a>
                              ) : (
                                <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer" className="attachment-file-link">
                                  <FileText size={18} />
                                  <div className="attachment-file-info">
                                    <span className="attachment-file-name">{msg.attachment_name || t("chatContent.file")}</span>
                                    {msg.attachment_size && (
                                      <span className="attachment-file-size">{formatFileSize(msg.attachment_size)}</span>
                                    )}
                                  </div>
                                  <Download size={16} />
                                </a>
                              )}
                            </div>
                          )}
                          <span className="message-time" title={formatFullTimestamp(msg.created_at)}>
                            {formatTime(msg.created_at)}
                            {isOwn && (
                              <span
                                className="read-receipt"
                                title={msg.is_read ? t("chatContent.read") : t("chatContent.delivered")}
                              >
                                {msg.is_read ? <CheckCheck size={13} /> : <Check size={13} />}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="message-input-area">
              {message && (
                <div className={`feedback-message ${message.includes("❌") ? "error" : "success"}`}>
                  {message}
                  <button onClick={() => setMessage("")} className="feedback-dismiss">
                    ×
                  </button>
                </div>
              )}
              {selectedFile && (
                <div className="attachment-preview">
                  {filePreview ? (
                    <img src={filePreview} alt="Preview" className="attachment-preview-image" />
                  ) : (
                    <div className="attachment-preview-file">
                      <FileText size={20} />
                      <span>{selectedFile.name}</span>
                    </div>
                  )}
                  <div className="attachment-preview-info">
                    <span className="attachment-preview-name">{selectedFile.name}</span>
                    <span className="attachment-preview-size">{formatFileSize(selectedFile.size)}</span>
                  </div>
                  <button
                    onClick={clearSelectedFile}
                    className="attachment-preview-remove"
                    title={t("chatContent.removeAttachment")}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              <div className="input-wrapper">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  accept="image/*,.pdf,.doc,.docx,.txt,.zip,.rar,.dst,.pes,.jef,.exp,.vp3,.hus,.sew,.xxx,.pec,.tap"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="attach-btn"
                  title={t("chatContent.attachFile")}
                  disabled={loading}
                >
                  <Paperclip size={20} />
                </button>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t("chatContent.typeMessage")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  disabled={loading}
                  rows="3"
                />
                <button
                  onClick={sendMessage}
                  disabled={(!newMessage.trim() && !selectedFile) || loading}
                  className="send-btn"
                >
                  {loading ? (
                    <Clock size={20} className="spin" />
                  ) : (
                    <Send size={20} />
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="no-conversation">
            <MessageCircle size={64} color="#D1D5DB" />
            <h3>{t("chatContent.selectConversation")}</h3>
            <p>{t("chatContent.chooseOrder")}</p>
          </div>
        )}
      </div>

      {/* Modal for starting new conversation (Customer only) */}
      {!isAdmin && showNewConvModal && (
        <div className="chat-modal-overlay" onClick={() => setShowNewConvModal(false)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chat-modal-header">
              <h3>{t("chatContent.startNewConversation")}</h3>
              <button onClick={() => setShowNewConvModal(false)} className="chat-modal-close">
                ×
              </button>
            </div>

            <div className="chat-modal-body">
              <p className="chat-modal-hint">{t("chatContent.selectOrderPrompt")}</p>

              {orders.length === 0 ? (
                <div className="chat-modal-empty">
                  <p>{t("chatContent.noOrders")}</p>
                </div>
              ) : (
                <div className="chat-modal-orders">
                  {orders.map((order) => (
                    <button
                      key={order.id}
                      onClick={() => createConversationForOrder(order.id)}
                      disabled={creatingConv}
                      className="chat-modal-order"
                    >
                      <div className="chat-modal-order-info">
                        <h4>{order.order_number}</h4>
                        <p>{order.design_name || t("chatContent.design")}</p>
                      </div>
                      <span className="status-badge" data-status={order.status}>
                        {order.status}
                      </span>
                      {creatingConv && <Loader2 size={16} className="spin" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatContent;
